import {SimpleSignal} from "../utils/SimpleSignal";
import {Project, IProjectConfig} from "./Project";
import {Collection} from "../utils/Collection";
import {XHRLoader} from "../utils/XHRLoader";
import {Main} from "../Main";
import {IProjectSettings} from "../config/IProjectSettings";
import {BrowserUtils} from "../utils/BrowserUtils";
import {ConversionUtils} from "../utils/ConversionUtils";
import {DateUtils} from "../utils/DateUtils";
import {WarningWindow} from "../ui/WarningWindow";
import {ConfirmWindow} from "../ui/ConfirmWindow";
import {CustomPopupWindow} from "ui/CustomPopupWindow";
import {PromptWindow} from "../ui/PromptWindow";
import {ObjectUtils} from "../utils/data/ObjectUtils";
import {AuthenticationView} from "./AuthenticationView";

export class Authentication
{
	private static projectLoadErrorMsg: string = "Your project could not be loaded. Please try again or proceed with a new project.";
	public static URL = Authentication.getPHPPath();

	private _authView: AuthenticationView;

	public static getPHPPath()
	{
		let currentBaseURL = BrowserUtils.getURLPathWithoutQuery();

		// trim the trailing "index.php" if exists
		const indexOfIndexPhp = currentBaseURL.indexOf("/index.php");
		if (indexOfIndexPhp > -1)
		{
			currentBaseURL = currentBaseURL.substring(0, indexOfIndexPhp);
		}

		if (currentBaseURL.charAt(currentBaseURL.length - 1) !== "/")
		{
			currentBaseURL += "/";
		}

		return `${currentBaseURL}php/`;
	}

	public loginChange: SimpleSignal;
	public loggedIn: boolean;

	private _email: string = "";
	private _pass: string = "";

	public projectsLoaded: SimpleSignal;
	private _projects: Collection<Project>;

	public selectedProjectChanged: SimpleSignal;
	private _selectedProject: Project;

	public needsRegisterFromFB: SimpleSignal;

	private _firstRefresh = true;

	// ======================================================================================================
	// Constructor

	// only called when core assets are loaded
	constructor()
	{
		this.loginChange = new SimpleSignal();
		this.projectsLoaded = new SimpleSignal();
		this.selectedProjectChanged = new SimpleSignal();
		this.needsRegisterFromFB = new SimpleSignal();

		this._selectedProject = Project.initialElement;
		this.clearProjects();

		this.initLogin();
	}

	private  initLogin()
	{
		this.trySavedLogin();
		//var projectInfoFromURL = Authentication.getProjectInfoFromURL();

		this.initProjectSettingsFromURL();
	}

	// ======================================================================================================
	//

	private initProjectSettingsFromURL()
	{
		// initial settings: default or url

		var projectInfo = Authentication.getProjectInfoFromURL();

		if (projectInfo)
		{
			// url found, try to retrieve project

			var xhr = XHRLoader.load({
				url: Authentication.URL + "get_project.php",
				method: XHRLoader.METHOD_GET,
				params: {
					email: projectInfo.email,
					project_name: projectInfo.project
				},
				onComplete: this.onInitialURLProjectLoaded,
				onFail: this.onInitialURLProjectFailed
			});
		}
		else
		{
			// no valid url, use default settings

			this.setInitialSettingsToDefault();
		}
	}



	private  onInitialURLProjectLoaded = async (xhr, result, params) =>
	{
		let project = null;
		if (!result.error && Array.isArray(result.message))
		{
			project = result.message[0];
		}

		if (project)
		{
			this._selectedProject = new Project(result.message[0]);

			this.setInitialSettings(this._selectedProject.settings);

			if(result.warning !== ""){
				await WarningWindow.open(result.warning);
			}
		}
		else
		{
			await WarningWindow.open(Authentication.projectLoadErrorMsg);
			this.setInitialSettingsToDefault();
		}
	};

	private onInitialURLProjectFailed = async (xhr, result, params) =>
	{
		await WarningWindow.open(Authentication.projectLoadErrorMsg);
		this.setInitialSettingsToDefault();
	};

	private setInitialSettingsToDefault()
	{
		this.setInitialSettings(Main.getInstance().config.config.defaultSettings);
	}

	private setInitialSettings(settings: IProjectSettings)
	{
		Project.initialElement.settings = Main.getInstance().config.config.defaultSettings;
		Main.getInstance().setProjectSettings(settings);
	}

	public static getProjectInfoFromURL(): {email: string; project: string}
	{
		var projectInfo: {email: string; project: string} = BrowserUtils.getQueryParams();

		if (projectInfo.email && projectInfo.project)
		{
			return projectInfo;
		}

		return null;
	}

	// ======================================================================================================
	// Login, logout

	private trySavedLogin(): string
	{
		this.login();

		if(this._email !== ""){
			return this._email;
		}

		return null;
	}

	public login()
	{
		// http://muellerinc.com/dev_june_db/getuser.php?email=grizzly33.levlist@gmail.com&userpass=asad21
		// returns string name or empty string if not found



		const xhr = XHRLoader.load({
			url: Authentication.URL + "login.php",
			method: XHRLoader.METHOD_GET,
			onComplete: this.onLoginResult,
			onFail: this.onLoginFail
		});
	}

	public checkSamlSession()
	{
		const xhr = XHRLoader.load({
			url: Authentication.URL + "login.php",
			method: XHRLoader.METHOD_GET,
			onComplete: this.onCheckSamlSessionResult,
			onFail: this.onCheckSamlSessionResultFail
		});
	}

	private onCheckSamlSessionResult = async (xhr: XMLHttpRequest, result) =>
	{
		const currentBaseURL = BrowserUtils.getCurrentBaseURL();
		if ((await this.checkResult(result)))
		{
			window.open(currentBaseURL, "_self");
		}
		else{
			// default is apps
			let url = `https://apps-dev.muellerinc.com/samlapp/logout-3d-relay.php?source=${currentBaseURL}`;

			if(Authentication.getPHPPath().includes("apps-prd")){
				url = `https://apps-prd.muellerinc.com/samlapp/logout-3d-relay.php?source=${currentBaseURL}`;
			}
			else if(Authentication.getPHPPath().includes("apps-dev")){
				url = `https://apps-dev.muellerinc.com/samlapp/logout-3d-relay.php?source=${currentBaseURL}`;
			}
			window.open(url, "_self");
		}
	};

	private onCheckSamlSessionResultFail = async () =>
	{
		await WarningWindow.open("Logout failed. Please reload the page and try again.");
	};

	public logout()
	{
		// default is apps
		let url = `https://apps.muellerinc.com/samlapp/logout-3d-relay.php?source=${window.location.hostname}`;

		if(Authentication.getPHPPath().includes("apps-prd")){
			url = `https://apps-prd.muellerinc.com/samlapp/logout-3d-relay.php?source=${window.location.hostname}`;
		}
		else if(Authentication.getPHPPath().includes("apps-dev")){
			url = `https://apps-dev.muellerinc.com/samlapp/logout-3d-relay.php?source=${window.location.hostname}}`;
		}
		window.open(url, "_self");

		this.onChangeLogin("");
	}

	private onLoginResult = async (xhr: XMLHttpRequest, result) =>
	{
		if (!(await this.checkResult(result)))
		{
			var name = result.message.name;
			var id = result.message.id;
			this._email = result.message.email;
			this.onChangeLogin(name, id);
		}
	};

	private async checkResult(result)
	{
		if (result.error)
		{
			this.setStatus("");
			//await WarningWindow.open(result.message);
			return true;
		}
		return false;
	}

	private onLoginFail = async () =>
	{
		await WarningWindow.open("Login failed. Please enter your username and password.");
	};

	private onChangeLogin(userName: string, id: string = "")
	{
		if (userName)
		{
			// log in
			this.loggedIn = true;
			this.loginChange.dispatch(userName);
		}
		else
		{
			// log out

			this._email = "";
			this._pass = "";

			this.loggedIn = false;
			this.loginChange.dispatch(null);
		}

		this.refreshProjects();
	}

	// ======================================================================================================
	// Forgotten password

	public sendForgottenPassword(email: string, callback: Function)
	{
		var xhr = XHRLoader.load({
			url: Authentication.URL + "send_forgotten_password.php",
			method: XHRLoader.METHOD_GET,
			params: {
				email: email
			},
			onComplete: callback,
			onFail: callback
		});
	}

	// ======================================================================================================
	// List projects

	private refreshProjects()
	{
		if (this._email)
		{
			var xhr = XHRLoader.load({
				url: Authentication.URL + "list_projects.php",
				method: XHRLoader.METHOD_GET,
				params: {
					email: this._email
				},
				onComplete: this.onRefreshProjects
			});
		}
		else
		{
			// not logged in

			this.clearProjects();
			this.selectProject(Project.initialElement);
		}
	}

	private clearProjects()
	{
		var emptyList = Project.createList([]);
		this.setProjects(emptyList);
	}

	private onRefreshProjects = (xhr, result) =>
	{
		var projectConfigs: IProjectConfig[] = result.message;
		var projects = Project.createList(projectConfigs);

		this.setProjects(projects);

		// if it's the first refresh -> select the url project if it exists
		if (this._firstRefresh)
		{
			this._firstRefresh = false;

			const projectSelect = this._authView.projectSelect.element;

			const projectInfo = Authentication.getProjectInfoFromURL();
			if (projectInfo)
			{
				const projectInfoName = projectInfo.project;
				if (projectInfoName)
				{
					if (projectInfo.email === this._email)
					{
						projectSelect.value = projectInfoName; //this one to prevent it to load it twice
					}

					if (projectSelect.value === "") // no project found on this name -> this is not the user's project
					{
						projectSelect.value = Project.initialElement.name; // label as Unsaved project, and the user can save it as their own
					}
					else
					{
						for (var i = 0; i < projects.array.length; ++i)
						{
							if (projects.array[i].name === projectInfoName)
							{
								this.selectedProjectChanged.dispatch(this._selectedProject);
								break;
							}
						}
					}
				}
			}
		}
	};

	private setProjects(projects: Collection<Project>)
	{
		this._projects = projects;
		this.projectsLoaded.dispatch(this._projects);
	}

	public async selectProjectByName(projectName: string)
	{
		var project = this._projects.getByKey(projectName);
		if (project)
		{
			await this.selectProject(project);
		}
		else
		{
			console.log("Warning! No project found by name: " + projectName);
		}
	}

	public doesCurrentConfigEqualsInitial(currentConfig: IProjectSettings = Main.getInstance().config.save())
	{
		return ObjectUtils.compare(Main.getInstance().config.config.defaultSettings, currentConfig);
	}

	private async selectProject(project: Project, checkForUnsaved = true)
	{
		const main = Main.getInstance();

		// check if there are unsaved changed

		let confirmed = true;

		if (checkForUnsaved)
		{
			if (!this.isSelectedProjectSaved() && !this.doesCurrentConfigEqualsInitial())
			{
				confirmed = await ConfirmWindow.open(`Are you sure you want to select a different project? Any unsaved changes will be lost!`);
			}
		}

		if (confirmed)
		{
			this._selectedProject = project;
			if (this._selectedProject.settings == null)
			{
				this._selectedProject.settings = Main.getInstance().config.config.defaultSettings;
			}
			main.setProjectSettings(this._selectedProject.settings);
			this.selectedProjectChanged.dispatch(this._selectedProject);

			this.updateProjectURL();
		}
		else
		{
			// undo project select
			this.selectedProjectChanged.dispatch(this._selectedProject);
		}
	}

	private updateProjectURL()
	{
		// Don't use this.isSelectedProjectInitial() here, because it checks the URL as well, and makes decisions based on that...
		if (this._selectedProject === Project.initialElement)
		{
			// clear hash
			window.history.replaceState("", "", "?");
		}
		else
		{
			// set hash to email, project name
			var info = {
				email: this._email,
				project: this._selectedProject.name
			};
			var hash = ConversionUtils.htmlEncode(info, "?");
			window.history.replaceState("", "", hash);
		}
	}

	// ======================================================================================================
	// Save project

	public async onSave()
	{
		if (this.isSelectedProjectInitial())
		{
			// create new project
			this.createProject();

			return false;
		}
		else
		{
			// save existing project

			return (await this.saveProject());
		}
	}

	public onSaveAs()
	{
		// save existing project as a new project

		this.createProject();
	}

	public onDelete()
	{
		// delete existing project
		this.deleteProject();
	}

	private async createProject(errorMessage: string = "")
	{
		// ask the project name

		const projectName = await PromptWindow.open(`${errorMessage}Project name:`, `New Project ${DateUtils.getCurrentDateString()}`, "Save", "Save");

		// check if a valid projectname was given (prompt returns null in case of cancel)
		if (projectName)
		{
			if (projectName === "Unsaved project")
			{
				await WarningWindow.open(`You can't name your project "Unsaved project". Please, choose a different project name!`);
				await this.createProject();
				return false;
			}
			var settings = Main.getInstance().config.save();
			var settingsJSON = JSON.stringify(settings);
			var image = Main.getInstance().content.getScreenShot(1200, 630);

			var xhr = XHRLoader.load({
				url: Authentication.URL + "create_project.php",
				method: XHRLoader.METHOD_POST,
				params: {
					email: this._email,
					password: this._pass,
					name: projectName,
					config: settingsJSON,
					image: image
				},
				onComplete: this.onProjectCreated
			});

			this.setStatus("Saving...");

			return true;
		}

		// User hasn't given a valid name
		return false;
	}

	private onProjectCreated = async (xhr: XMLHttpRequest, result: any, config: any) =>
	{
		if (result.error)
		{
			this.setStatus("");
			await this.createProject(`${result.message}<br><br>`);
			return;
		}

		const success = await Main.getInstance().saveSideImages(result.message.name);

		if (!success)
		{
			return;
		}

		// project successfully created on server
		// -> add locally

		const project = new Project({
			id: result.message.id,
			name: result.message.name,
			date: result.message.project_date,
			settings: JSON.parse(config.params.config) // todo
		});

		this._projects.add(project);

		// it's a save as or a new project create -> don't prompt for unsaved changes
		this.selectProject(project, false);

		//this.setStatus("Saved!", true);
		this.shareProject(result.message.name);
		//location.reload(true);
	};

	private async shareProject(projectName: string)
	{
		await CustomPopupWindow.open(`Project ${projectName} saved.`,
			`<div class="vbox">
			<p>
			Share your project:
			</p>
			<table style="margin-left: -25px;">
			  <tr>
				<td align="center" style="cursor: pointer;" onclick="main.sharingManager.onShareFacebook();"><img class="shareBtn" src="assets/social/facebook.svg" width="40px" height="40px"><br/>Facebook</td>
				<td align="center" style="cursor: pointer;" onclick="main.sharingManager.onShareTwitter();"><img class="shareBtn" src="assets/social/twitter.svg" width="40px" height="40px"><br/>Twitter</td>
			  </tr>
			  <tr>
				<td align="center" style="cursor: pointer;" onclick="main.sharingManager.onSharePinterest();"><img class="shareBtn" src="assets/social/pinterest.svg" width="40px" height="40px"><br/>Pinterest</td>
				<td align="center" style="cursor: pointer;" onclick="main.sharingManager.onShareEmail();document.querySelector('div[isOkButtonOnPopup=true]').click();"><img class="shareBtn" src="assets/social/email.svg" width="40px" height="40px"><br/>Email</td>			    
			  </tr>
			  <tr>
			    <td align="center" colspan=2><br/>Copy the below link to share:<br/><input type=text size=40 value=\"${await Main.getInstance().sharingManager.getPageUrl(false, false)}\" /></td>
			  </tr>
			</table>
			</div>`,
		true, "Close", null, false, "SaveProjectPopup");
	};

	private async deleteProject(errorMessage: string = "")
	{
		const submittedText = await PromptWindow.open(`${errorMessage}Type DELETE to confirm deletion of project. This action is not permanent. Project can be recovered by Mueller.`, "", "Delete", "Delete");
		// check if a valid projectname was given (prompt returns null in case of cancel)
		if (submittedText?.toUpperCase() === "DELETE")
		{
			const project = this._selectedProject;
			const settings = Main.getInstance().config.save();
			const settingsJSON = JSON.stringify(settings);
			const image = Main.getInstance().content.getScreenShot(1200, 630);
			const xhr = XHRLoader.load({
				url: Authentication.URL + "delete_project.php",
				method: XHRLoader.METHOD_POST,
				params: {
					email: this._email,
					password: this._pass,
					id: project.id,
					config: settingsJSON,
					image: image
				},
				onComplete: this.onProjectDeleted
			});
			this.setStatus("Deleting...");
		}
		else if (submittedText !== null)
		{
			this.deleteProject(`<span style="color: #FF4444; font-size: 25px;">Invalid entry - try again.</span><br><br>`);
		}
	}

	private onProjectDeleted = async (xhr, result, config) =>
	{
		if (await this.checkResult(result))
		{
			return;
		}

		this.setStatus("Deleted!", true);
		this.refreshProjects(); // refresh the dropdown list
		// project successfully saved on server
		this.selectProject(Project.initialElement);
	}

	private saveProject()
	{
		return new Promise<boolean>((resolve, reject) =>
		{
			var project = this._selectedProject;
			var settings = Main.getInstance().config.save();
			var settingsJSON = JSON.stringify(settings);
			var image = Main.getInstance().content.getScreenShot(1200, 630);

			var xhr = XHRLoader.load({
				url: Authentication.URL + "save_project.php",
				method: XHRLoader.METHOD_POST,
				params: {
					email: this._email,
					password: this._pass,
					id: project.id,
					config: settingsJSON,
					image: image
				},
				onComplete: async (xhr: XMLHttpRequest, result: any, config: any) =>
				{
					const wasSuccessful = await this.onProjectSaved(xhr, result, config);

					let success = false;
					if (wasSuccessful)
					{
						success = await Main.getInstance().saveSideImages(this._selectedProject.name);
						project.settings = settings;
					}
					else{
						await WarningWindow.open(result.message); //TODO not too nice, but php responnds with status code 200, changing that function breaks other functionality
					}

					resolve(success);
				},
				onFail: () =>
				{
					resolve(false);
				}
			});

			this.setStatus("Saving...");
		});
	}

	public saveProjectToLocalStorage = () =>
	{
		const settings = Main.getInstance().config.save();
		const settingsJSON = JSON.stringify(settings);
		localStorage.setItem("3DProject", settingsJSON);
		const urlParams = new URLSearchParams(window.location.search);
		const email = urlParams.get('email');
		const projectName = urlParams.get('project');
		localStorage.setItem("email", email);
		localStorage.setItem("projectName", projectName);
	};

	public getProjectFromLocalStorage = () =>
	{
		const ret = localStorage.getItem("3DProject");
		localStorage.removeItem("3DProject");
		return ret;
	};

	public getProjectEmailFromLocalStorage = () =>{
		const ret = localStorage.getItem("email");
		localStorage.removeItem("email");
		return ret;
	}

	public getProjectNameFromLocalStorage = () =>{
		const ret = localStorage.getItem("projectName");
		localStorage.removeItem("projectName");
		return ret;
	}

	private onProjectSaved = async (xhr: XMLHttpRequest, result: any, config: any) =>
	{
		if (await this.checkResult(result))
		{
			return false;
		}

		this.setStatus("Saved!", true);

		return true;

		// project successfully saved on server
		// -> add locally
	};

	private setStatus = (message: string = "", fadeOut = false) =>
	{
		const wrapper = document.getElementById("statusMessageWrapper") as HTMLDivElement;
		document.getElementById("statusMessage").textContent = message;

		const background = document.getElementById("popupBackground") as HTMLDivElement;
		if (message === "")
		{
			wrapper.classList.add("hidden");
			background.classList.add("hidden");
		}
		else
		{
			wrapper.classList.remove("hidden");
			background.classList.remove("hidden");
		}


		if (fadeOut)
		{
			setTimeout(this.setStatus, 4000);
		}
	};

	public setView(authView: AuthenticationView)
	{
		this._authView = authView;
	}

	public get projects()
	{
		return this._projects;
	}

	public get selectedProject()
	{
		return this._selectedProject;
	}

	public get email()
	{
		return this._email;
	}

	public isCurrentProjectInitial()
	{
		const urlProjectParams = Authentication.getProjectInfoFromURL();
		if (urlProjectParams)
		{
			return false;
		}

		return (
			!this._authView?.projectSelect.value ||
			this._authView?.projectSelect.value as any === Project.initialElement.name ||
			this._authView?.projectSelect.value?.name === Project.initialElement.name
		);
	}

	public isSelectedProjectInitial()
	{
		return this._authView?.projectSelect.element?.value === Project.initialElement.name;
	}

	public isSelectedProjectSaved()
	{
		if (this.isCurrentProjectInitial())
		{
			return false;
		}
		var main = Main.getInstance();
		return main.config.isSaved(this._selectedProject.settings);
	}

}
